import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Send } from "lucide-react";
import { portfolio } from "@/data/portfolio";
import useReveal from "@/hooks/useReveal";

const API = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`
  : "/api";
const initialForm = { name: "", email: "", subject: "", message: "" };

const Marquee = () => {
  const line = portfolio.currently.map((item, index) => (
    <span key={index} className="inline-flex items-center">
      <span className="font-serif italic text-2xl md:text-4xl leading-none px-5" style={{ color: "var(--ink)" }}>
        {item}
      </span>
      <span className="opacity-50">◆</span>
    </span>
  ));

  return (
    <div className="overflow-hidden hairline-b hairline py-5" data-testid="currently-marquee">
      <div className="marquee-track marquee-fast">
        {line}
        {line}
      </div>
    </div>
  );
};

const Contact = () => {
  const { contact, meta } = portfolio;
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const refHead = useReveal();
  const refContent = useReveal();

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const selectInquiry = (subject) => {
    setForm((current) => ({ ...current, subject }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "" });

    try {
      await axios.post(`${API}/contact`, form);
      setForm(initialForm);
      setStatus({ state: "success", message: "Message sent. I will reply within 24 hours." });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail[0]?.msg || "Please check the form fields."
        : typeof detail === "string"
          ? detail
          : "Could not send your message. Please email me directly.";
      setStatus({ state: "error", message });
    }
  };

  return (
    <section
      id="signal"
      data-testid="contact-section"
      className="px-5 md:px-10 pt-14 md:pt-28 pb-20 md:pb-32"
    >
      <div className="mx-auto max-w-[1400px]">
        <div className="flex items-baseline justify-between mb-6 md:mb-10">
          <div className="section-tag">
            <span style={{ color: "var(--ink)" }}>{contact.section}</span>
            <span className="ml-3">{contact.eyebrow}</span>
          </div>
          <div className="section-tag">→ Reply within 24h</div>
        </div>

        <h2 ref={refHead} className="reveal-up display text-4xl sm:text-5xl md:text-7xl lg:text-9xl max-w-[15ch] leading-[0.92]">
          Let&rsquo;s build{" "}
          <span className="display-italic" style={{ color: "var(--accent)" }}>something good.</span>
        </h2>

        <div ref={refContent} className="reveal-up mt-10 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="md:col-span-5">
            <p className="font-serif text-lg md:text-xl leading-relaxed max-w-[44ch]" style={{ color: "var(--ink-2)" }}>
              {contact.body}
            </p>
            <div className="mt-8 space-y-4">
              <div>
                <div className="eyebrow mb-1">Inbox</div>
                <a href={`mailto:${meta.email}`} data-testid="contact-email" className="font-serif text-2xl md:text-3xl link-ed">
                  {meta.email}
                </a>
              </div>
              <div>
                <div className="eyebrow mb-1">Phone</div>
                <a href={`tel:${meta.phone.replace(/\s+/g, "")}`} data-testid="contact-phone" className="font-serif text-lg link-ed">
                  {meta.phone}
                </a>
              </div>
              <div>
                <div className="eyebrow mb-1">Place</div>
                <div className="font-serif text-lg">{meta.location}</div>
                <div className="font-mono text-[0.72rem] uppercase tracking-microcap mt-1" style={{ color: "var(--ink-muted)" }}>
                  {meta.coords}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Status</div>
                <div className="font-serif text-lg">{meta.status}</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="hairline-b pb-3 mb-8 flex items-center justify-between" style={{ borderColor: "var(--rule)" }}>
              <div className="eyebrow">Send a signal</div>
              <div className="eyebrow">Secure form</div>
            </div>

            <div className="flex flex-wrap gap-2 mb-7" data-testid="inquiry-types">
              {contact.inquiryTypes.map((type, index) => {
                const selected = form.subject === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => selectInquiry(type)}
                    data-testid={`inquiry-${index}`}
                    className="font-mono text-[0.72rem] uppercase tracking-microcap px-3 py-2 rounded-full border transition-colors"
                    style={{
                      borderColor: selected ? "var(--ink)" : "var(--rule)",
                      color: selected ? "var(--paper)" : "var(--ink-2)",
                      background: selected ? "var(--ink)" : "transparent",
                    }}
                  >
                    {type}
                  </button>
                );
              })}
            </div>

            <form onSubmit={submitForm} data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              <label className="block">
                <span className="eyebrow">Name</span>
                <input className="line-input" type="text" name="name" value={form.name} onChange={updateField} placeholder="Your name" maxLength={120} required />
              </label>
              <label className="block">
                <span className="eyebrow">Email</span>
                <input className="line-input" type="email" name="email" value={form.email} onChange={updateField} placeholder="you@company.com" required />
              </label>
              <label className="block sm:col-span-2">
                <span className="eyebrow">Subject</span>
                <input className="line-input" type="text" name="subject" value={form.subject} onChange={updateField} placeholder="What would you like to discuss?" maxLength={200} />
              </label>
              <label className="block sm:col-span-2">
                <span className="eyebrow">Message</span>
                <textarea className="line-input resize-y min-h-[130px]" name="message" value={form.message} onChange={updateField} placeholder="Tell me about the role or project." rows={4} maxLength={5000} required />
              </label>

              <div className="sm:col-span-2 pt-2 flex flex-col sm:flex-row sm:items-center gap-4">
                <button type="submit" data-testid="contact-cta" className="btn-ink disabled:opacity-60 disabled:cursor-not-allowed" disabled={status.state === "loading"}>
                  {status.state === "loading" ? "Sending..." : `Send to ${meta.nameSplit.first.toLowerCase()}`}
                  <Send size={15} />
                </button>
                <div aria-live="polite" className="font-mono text-xs" style={{ color: status.state === "error" ? "var(--accent)" : "var(--ink-muted)" }}>
                  {status.message}
                </div>
              </div>
            </form>

            <div className="mt-12 grid grid-cols-2 gap-6">
              <a href={meta.linkedin} target="_blank" rel="noopener noreferrer" className="link-ed font-serif text-lg" data-testid="social-linkedin">
                LinkedIn / <ArrowUpRight className="inline" size={14} />
              </a>
              <a href={`mailto:${meta.email}`} className="link-ed font-serif text-lg" data-testid="social-email">Email /</a>
              <a href={`tel:${meta.phone.replace(/\s+/g, "")}`} className="link-ed font-serif text-lg" data-testid="social-phone">Phone /</a>
              <a href={meta.linkedin} target="_blank" rel="noopener noreferrer" className="link-ed font-serif text-lg" data-testid="social-resume">Résumé /</a>
            </div>
          </div>
        </div>

        <div className="mt-20 md:mt-28">
          <Marquee />
        </div>
      </div>
    </section>
  );
};

export default Contact;
